"use client";

// Pending-trade banner — shown on the homepage and /sell to give
// users a one-tap path back to any non-terminal trade they have.
//
// The most common recovery scenario: an anonymous user closes their
// tracking tab thinking the trade is done, then realises they still
// need to send crypto (or check status). The browser still has the
// sessionToken cookie, so this banner finds and surfaces the trade
// without any ID lookup or email recovery loop.
//
// Renders nothing when:
//   - The fetch hasn't completed yet (no flash of empty card)
//   - The user has no pending trades
//   - The fetch fails (silently — better empty than a broken banner)

import { useEffect, useState } from "react";
import Link from "next/link";
import { ArrowRight, Clock } from "lucide-react";
import { formatLocalAmount } from "@/lib/locale";

type PendingTrade = {
  id: string;
  cryptoType: string;
  cryptoAmount: number;
  network: string;
  depositAddressTruncated: string | null;
  localCurrency: string;
  localAmount: number;
  status: string;
  expiresAt: string | null;
  createdAt: string;
};

const STATUS_LABEL: Record<string, string> = {
  pending_deposit:       "Waiting for your deposit",
  deposit_confirmed:     "Deposit received — processing",
  processing_payout:     "Sending payout",
  awaiting_approval:     "Manual review",
  awaiting_verification: "Verification needed",
  action_required:       "Action required",
};

const STATUS_TONE: Record<string, string> = {
  pending_deposit:       "border-warning-border bg-warning-bg/40 text-warning-fg",
  deposit_confirmed:     "border-info-border bg-info-bg/40 text-info-fg",
  processing_payout:     "border-info-border bg-info-bg/40 text-info-fg",
  awaiting_approval:     "border-warning-border bg-warning-bg/40 text-warning-fg",
  awaiting_verification: "border-warning-border bg-warning-bg/40 text-warning-fg",
  action_required:       "border-danger-border bg-danger-bg/40 text-danger-fg",
};

export function PendingTradeBanner() {
  const [trades, setTrades] = useState<PendingTrade[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/v1/transactions/pending-mine", { cache: "no-store" })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setTrades(Array.isArray(d?.trades) ? d.trades : []);
      })
      .catch(() => {
        // Silent failure — banner is opportunistic. A 503 on this
        // endpoint shouldn't ruin the homepage.
        if (!cancelled) setTrades([]);
      });
    return () => { cancelled = true; };
  }, []);

  // Don't render until we have a definitive answer. Avoids a flash
  // of empty state on slow connections.
  if (!trades || trades.length === 0) return null;

  // Most common case: exactly one pending trade. Render it as a
  // hero card. Multiple trades fall back to a stacked list.
  if (trades.length === 1) {
    const t = trades[0];
    const tone = STATUS_TONE[t.status] ?? "border-border bg-muted/40 text-foreground";
    return (
      <div className={`rounded-2xl border-2 px-4 py-3 mb-5 ${tone}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="space-y-1 min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-80">
              <Clock size={11} />
              {STATUS_LABEL[t.status] ?? t.status}
            </div>
            <p className="text-sm font-semibold text-foreground">
              {fmtAmount(t.cryptoAmount)} {t.cryptoType} → {t.localCurrency}{" "}
              {formatLocalAmount(t.localAmount, t.localCurrency)}
            </p>
            <p className="text-[11px] opacity-80">
              Network: {t.network} · Started {fmtRelative(t.createdAt)}
              {t.expiresAt && t.status === "pending_deposit" && ` · Expires ${fmtTime(t.expiresAt)}`}
            </p>
          </div>
          <Link
            href={`/transactions/${t.id}`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-3.5 py-2 text-sm font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            Resume <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    );
  }

  // Multi-trade fallback — compact list. Same per-row treatment.
  return (
    <div className="rounded-2xl border-2 border-warning-border bg-warning-bg/30 px-4 py-3 mb-5 space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-warning-fg">
        You have {trades.length} pending trades
      </p>
      <div className="space-y-1.5">
        {trades.map((t) => (
          <Link
            key={t.id}
            href={`/transactions/${t.id}`}
            className="flex items-center justify-between gap-3 rounded-lg bg-background/80 hover:bg-background border border-border px-3 py-2 transition-colors"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">
                {fmtAmount(t.cryptoAmount)} {t.cryptoType} → {t.localCurrency}{" "}
                {formatLocalAmount(t.localAmount, t.localCurrency)}
              </p>
              <p className="text-[11px] text-muted-foreground">
                {STATUS_LABEL[t.status] ?? t.status} · {t.network}
              </p>
            </div>
            <ArrowRight size={14} className="text-muted-foreground shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}

// Format helpers — kept local so the banner can be imported anywhere
// without dragging in lib/locale.

function fmtAmount(n: number): string {
  // Trim trailing zeros after 8dp; up to 8 sig figs of crypto.
  return n.toFixed(8).replace(/\.?0+$/, "");
}

function fmtRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}
